import type { MouseEventHandler } from 'react';
import { useState } from 'react';

import {
  BpkButtonV2,
  BUTTON_TYPES,
} from '@skyscanner/backpack-web/bpk-component-button';
import withInfiniteScroll from '@skyscanner/backpack-web/bpk-component-infinite-scroll';

import ReviewCardList from './ReviewCardsList';

import type { I18nService } from '@shared/types/i18n';

import STYLES from './InfiniteScrollReviews.module.scss';

import useDataSource from '@hotels-hotel-detail/data-access/hooks/useDataSource';
import {
  useHotelReviewsContext,
  useReviews,
} from '@hotels-hotel-detail/data-access/hooks/useHotelReviewsContext';
import { REVIEWS_PER_PAGE_MOBILE } from '@hotels-hotel-detail/utils/review-service';
import CenteredSpinner from '@shared/ui/components/CenteredSpinner';
import { withI18n } from '@shared/ui/contexts/i18n';

const InfiniteReviewCardList = withInfiniteScroll(ReviewCardList);

type Props = {
  i18n: I18nService;
  onScrollFinished: MouseEventHandler<HTMLButtonElement>;
};

const InfiniteScrollReviews = ({
  i18n: { translate },
  onScrollFinished,
}: Props) => {
  const { fetchData, isLoading, reload } = useHotelReviewsContext();
  const reviews = useReviews();
  const [error, setError] = useState(false);

  const onDataFetchFailed = () => {
    setError(true);
  };

  const dataSource = useDataSource({
    initialData: reviews,
    onFetchFailed: onDataFetchFailed,
    fetchData,
  });

  return isLoading ? (
    <CenteredSpinner className={STYLES.InfiniteScrollReviews__spinner} />
  ) : (
    <>
      <InfiniteReviewCardList
        dataSource={dataSource}
        onScrollFinished={onScrollFinished}
        renderLoadingComponent={() => <CenteredSpinner />}
        loaderIntersectionTrigger="small"
        elementsPerScroll={REVIEWS_PER_PAGE_MOBILE}
        initiallyLoadedElements={reviews.length}
      />
      {error && (
        <div className={STYLES.InfiniteScrollReviews__bottomButton}>
          <BpkButtonV2
            type={BUTTON_TYPES.secondary}
            onClick={reload}
            aria-label={translate('Button_refreshSearch')}
          >
            {translate('Button_refreshSearch')}
          </BpkButtonV2>
        </div>
      )}
    </>
  );
};

export default withI18n(InfiniteScrollReviews);
